import { ReactElement, useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { AuxScatterSeries } from './auxSensorChannels';

interface AuxComparisonPlotProps {
  title: string;
  xLabel: string;
  yLabel: string;
  unit: string;
  series: AuxScatterSeries[];
  axisMax: number;
  isLoading?: boolean;
}

const SCATTER_COLORS = ['#3B82F6', '#10B981', '#F59E0B'];
const REFERENCE_COLOR = '#9CA3AF';

/**
 * Sensor-agreement scatter: each series is plotted against wheel sensor 1 on the
 * x-axis. A dashed 1:1 reference line is overlaid (via a mixed scatter+line
 * chart) so perfect agreement reads as points lying on the diagonal.
 */
export default function AuxComparisonPlot({
  title,
  xLabel,
  yLabel,
  unit,
  series,
  axisMax,
  isLoading = false,
}: AuxComparisonPlotProps): ReactElement {
  const chartSeries = useMemo(
    () => [
      ...series.map((s) => ({ name: s.name, type: 'scatter' as const, data: s.points })),
      { name: '1:1', type: 'line' as const, data: [[0, 0], [axisMax, axisMax]] },
    ],
    [series, axisMax],
  );

  const options = useMemo(
    () => ({
      chart: {
        type: 'line' as const,
        height: 380,
        animations: { enabled: false },
        toolbar: {
          show: true,
          tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true },
        },
        zoom: { enabled: true, type: 'xy' as const },
      },
      colors: [...series.map((_, i) => SCATTER_COLORS[i % SCATTER_COLORS.length]), REFERENCE_COLOR],
      markers: { size: [...series.map(() => 3), 0], fillOpacity: 0.5, strokeWidth: 0 },
      stroke: { width: [...series.map(() => 0), 2], dashArray: [...series.map(() => 0), 6], curve: 'straight' as const },
      xaxis: {
        type: 'numeric' as const,
        min: 0,
        max: axisMax || undefined,
        tickAmount: 8,
        title: { text: xLabel, style: { fontSize: '13px', fontWeight: 600 } },
        labels: { formatter: (value: string) => `${parseFloat(value).toFixed(0)}` },
      },
      yaxis: {
        min: 0,
        max: axisMax || undefined,
        title: { text: yLabel, style: { fontSize: '13px', fontWeight: 600 } },
        labels: { formatter: (value: number) => (value != null ? value.toFixed(0) : '') },
      },
      grid: { borderColor: '#E5E7EB', strokeDashArray: 5 },
      legend: { position: 'top' as const, horizontalAlign: 'left' as const },
      tooltip: {
        shared: false,
        intersect: true,
        x: { formatter: (value: number) => `${value.toFixed(1)} ${unit}` },
        y: { formatter: (value: number) => (value != null ? `${value.toFixed(1)} ${unit}` : '') },
      },
    }),
    [series, axisMax, xLabel, yLabel, unit],
  );

  return (
    <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md">
      <h2 className="cr-mb-2 cr-text-lg cr-font-semibold cr-text-blue-900">{title}</h2>
      {isLoading ? (
        <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-gap-2 cr-text-gray-400">
          <div
            className="cr-h-6 cr-w-6 cr-animate-spin cr-rounded-full cr-border-2
              cr-border-cirrusBlue-200 cr-border-t-cirrusBlue-400"
          />
          <span className="cr-text-sm">Loading comparison…</span>
        </div>
      ) : series.length === 0 ? (
        <div className="cr-flex cr-h-32 cr-items-center cr-justify-center cr-text-sm cr-text-gray-400">
          No comparison data available for this run.
        </div>
      ) : (
        <ReactApexChart options={options} series={chartSeries} type="line" height={380} />
      )}
    </div>
  );
}
